/**
 * shared/campaign_contract.ts — CLIENT MIRROR of server/src/lib/campaign_contract.ts
 * (the server copy is authoritative; keep them byte-identical below the headers).
 * Space campaign slice C (2026-09-30) — THE CAMPAIGN CONTRACT: the one retreat
 * rule, the round as a pure step the server executes, the drain-before-judge
 * replay, the honest late window, the journal's bounds and the after-action
 * record's summary + checksum. Pure.
 */
import {
  BATTLE_MIN_S, BATTLE_END_S, BATTLE_LEN_S, ROUND_S, mayRetreat, attritionRound, escortRound, pickCapitalLoss,
  pickEscortLoss, ESCORT_CLASSES, roundsEligible, roundAtOf, CAMPAIGN_POINTS, CAMPAIGN_POINTS_CAP, HULLS_PER_FRIGATE,
  HULLS_PER_CORVETTE, type EscortClass,
} from './space_campaign';
import { STANCES, STANCE_FACTORS, STANCE_ROUNDS, chooseStance, type Stance } from './space_ops';

// ── Versions ─────────────────────────────────────────────────────────────────
/** The campaign rules a row was opened under (0 = a campaign that pre-dates the contract: its journal starts mid-war). */
export const CAMPAIGN_RULES_VERSION = 1;
/** The rollout flag for the shadow journal and the after-action record (ABSENT = ON). */
export const CAMPAIGN_JOURNAL_FLAG = 'campaign_journal_v1';
/** A harness campaign lives on a cycle no clock will ever reach (≈ year 104,000); ≡ 0 (mod 3) so its fort is the
 *  forced window's (cyc 424242 → poi9) and a probe's forced picture and the server's zone agree. */
export const SANDBOX_CYC_BASE = 900_000_000;
export function isSandboxCyc(cyc: number | bigint): boolean { return Number(cyc) >= SANDBOX_CYC_BASE; }

// ── THE ONE RETREAT RULE ─────────────────────────────────────────────────────
/** Every path that can end a line: the minute's round, a pilot's receipt, a validated hit (slice D), a lever. */
export type RetreatPath = 'round' | 'receipt' | 'validated' | 'debug';
export const RETREAT_PATHS: readonly RetreatPath[] = ['round', 'receipt', 'validated', 'debug'];
export interface RetreatSide { capsAlive: number; capsCommitted: number; stance: string }
export interface RetreatDecision {
  /** −1 = no retreat (none eligible, or BOTH — the window then decides). */
  retreatSide: -1 | 0 | 1;
  eligible: [boolean, boolean];
  margins: [number, number];
  path: RetreatPath;
  /** A sentence for the journal, the AAR and the HUD. */
  reason: string;
}
/** A stance's retreat margin (withdraw +1); an unknown / absent stance reads as screen (margin 0), as the server's
 *  stance reader has always defaulted. */
export function stanceMargin(stance: string): number {
  return (STANCES as readonly string[]).includes(stance) ? STANCE_FACTORS[stance as Stance].retreatMargin : 0;
}
const SIDE_NAME = ['Concord', 'Compact'] as const;
/** THE ONE RETREAT RULE (2026-09-30, report §9.2): the same counts, stances and elapsed time give the same decision on
 *  EVERY path — each side's margin is its OWN stance's, a line with no capitals always may leave, exactly one eligible
 *  side retreats, and when both may the war runs to its window. `path` is recorded, never read by the arithmetic. */
export function retreatDecision(sides: readonly [RetreatSide, RetreatSide], elapsedS: number, path: RetreatPath = 'round'): RetreatDecision {
  const margins: [number, number] = [stanceMargin(sides[0].stance), stanceMargin(sides[1].stance)];
  const eligible: [boolean, boolean] = [
    mayRetreat(sides[0].capsAlive, sides[0].capsCommitted, elapsedS, margins[0]),
    mayRetreat(sides[1].capsAlive, sides[1].capsCommitted, elapsedS, margins[1]),
  ];
  const retreatSide: -1 | 0 | 1 = eligible[0] !== eligible[1] ? (eligible[0] ? 0 : 1) : -1;
  let reason = '';
  if (retreatSide >= 0) {
    const rs = retreatSide as 0 | 1;
    const s = sides[rs];
    reason = s.capsAlive <= 0
      ? `the ${SIDE_NAME[rs]} has no capitals left — a line with none always leaves`
      : `the ${SIDE_NAME[rs]} is down to ${s.capsAlive}/${s.capsCommitted} capitals after ${Math.floor(elapsedS)} s (${s.stance || 'screen'}${margins[rs] ? ` +${margins[rs]}` : ''})`;
  } else if (eligible[0] && eligible[1]) reason = 'both lines may leave — neither yields; the window decides';
  return { retreatSide, eligible, margins, path, reason };
}

// ── THE HONEST LATE WINDOW ───────────────────────────────────────────────────
/** A campaign opened late keeps its REAL openedAt; can its 15-minute retreat floor still be reached by a round? (A
 *  round runs no later than the finale cutoff, endsAt − 32 s.) */
export function floorReachable(openedAtS: number, endsAtS: number): boolean {
  return endsAtS - BATTLE_END_S - openedAtS >= BATTLE_MIN_S;
}
/** How late a campaign opened into its window (0 when on time), and the war it really has. */
export function lateWindow(openedAtS: number, endsAtS: number): { lateS: number; warS: number; floorReachable: boolean } {
  const warS = Math.max(0, endsAtS - openedAtS);
  return { lateS: Math.max(0, BATTLE_LEN_S - warS), warS, floorReachable: floorReachable(openedAtS, endsAtS) };
}

// ── THE ROUND AS A PURE STEP (the server executes this; the harness replays it) ──
export interface SideSnap { capsAlive: number; capsCommitted: number; skirmAlive: number; skLost: number; capLost: number }
export interface LifeSnap { side: number; slot: number; alive: boolean }
export interface EscortSnap extends LifeSnap { cls: string }
export interface RoundSnap {
  campaignId: bigint;
  openedAtS: number;
  endsAtS: number;
  rounds: number;
  state: string;
  sides: [SideSnap, SideSnap];
  /** The stances on record (null = no row: a campaign opened before the stances, or re-armed by a lever). */
  stances: [Stance | null, Stance | null];
  /** Each civ's reserve capitals at the round (chooseStance reads them). */
  reserveCaps: [number, number];
  caps: readonly LifeSnap[];
  escorts: readonly EscortSnap[];
}
export interface RoundStep {
  /** The 0-based round index the hashes read (the row's `rounds` before the step). */
  round: number;
  roundAtS: number;
  /** The opening stances written now (null when the rows existed). */
  opening: [Stance, Stance] | null;
  openingReason: [string, string] | null;
  /** Stance changes at a STANCE_ROUNDS boundary (side, the new stance, the reason). */
  changes: Array<{ side: 0 | 1; stance: Stance; reason: string }>;
  stances: [Stance, Stance];
  mult: [number, number];
  sk: [number, number];
  capDeaths: Array<{ side: 0 | 1; slot: number }>;
  escortDeaths: Array<{ side: 0 | 1; cls: EscortClass; slot: number }>;
  /** The sides after the step (the counts the server writes). */
  sides: [SideSnap, SideSnap];
  retreat: RetreatDecision;
}
function livingOf(lives: readonly LifeSnap[], side: number): number[] {
  return lives.filter((r) => r.side === side && r.alive).map((r) => r.slot).sort((x, y) => x - y);
}
function livingEscortsOf(rows: readonly EscortSnap[], side: number, cls: string): number[] {
  return rows.filter((r) => r.side === side && r.cls === cls && r.alive).map((r) => r.slot).sort((x, y) => x - y);
}
/** ONE attrition round at its LOGICAL minute — exactly the pre-contract runRound's arithmetic (the harness carries that
 *  code as the oracle): the stances refresh every STANCE_ROUNDS from the PRE-loss counts, `attritionRound` scales by
 *  take × give, each capital loss names ONE living slot, each escort class rolls its own named loss, the pool loses
 *  its gunships — and only THEN the one retreat rule judges both sides at the round's minute. */
export function campaignRound(snap: RoundSnap, roundAtS: number): RoundStep {
  const r = snap.rounds;
  const [a, b] = snap.sides;
  let opening: [Stance, Stance] | null = null, openingReason: [string, string] | null = null;
  const st: [Stance, Stance] = [snap.stances[0] ?? 'screen', snap.stances[1] ?? 'screen'];
  if (snap.stances[0] === null && snap.stances[1] === null) {
    opening = [
      chooseStance(a.capsAlive, b.capsAlive, snap.reserveCaps[0], r),
      chooseStance(b.capsAlive, a.capsAlive, snap.reserveCaps[1], r),
    ];
    openingReason = [
      `round ${r}: ${a.capsAlive} v ${b.capsAlive} capitals, ${snap.reserveCaps[0]} in reserve`,
      `round ${r}: ${b.capsAlive} v ${a.capsAlive} capitals, ${snap.reserveCaps[1]} in reserve`,
    ];
    st[0] = opening[0]; st[1] = opening[1];
  }
  const changes: RoundStep['changes'] = [];
  if (r > 0 && r % STANCE_ROUNDS === 0) {
    for (const side of [0, 1] as const) {
      const ours = side === 0 ? a.capsAlive : b.capsAlive, theirs = side === 0 ? b.capsAlive : a.capsAlive;
      const next = chooseStance(ours, theirs, snap.reserveCaps[side], r);
      if (next !== st[side]) { changes.push({ side, stance: next, reason: `round ${r}: ${ours} v ${theirs}` }); st[side] = next; }
    }
  }
  const mult: [number, number] = [STANCE_FACTORS[st[0]].take * STANCE_FACTORS[st[1]].give, STANCE_FACTORS[st[1]].take * STANCE_FACTORS[st[0]].give];
  const loss = attritionRound(snap.campaignId, r, [a.capsAlive, b.capsAlive], mult);
  const capDeaths: RoundStep['capDeaths'] = [];
  const capKilled: [number, number] = [0, 0];
  for (const side of [0, 1] as const) {
    if (loss.cap[side] < 1) continue;
    const slot = pickCapitalLoss(snap.campaignId, r, side, livingOf(snap.caps, side));
    if (slot >= 0) { capDeaths.push({ side, slot }); capKilled[side] = 1; }
  }
  const escortDeaths: RoundStep['escortDeaths'] = [];
  for (const side of [0, 1] as const) {
    const er = escortRound(snap.campaignId, r, side, side === 0 ? b.capsAlive : a.capsAlive, mult[side]);
    for (const cls of ESCORT_CLASSES) {
      if (!er[cls]) continue;
      const slot = pickEscortLoss(snap.campaignId, r, side, cls, livingEscortsOf(snap.escorts, side, cls));
      if (slot >= 0) escortDeaths.push({ side, cls, slot });
    }
  }
  const sides = [a, b].map((s, i) => ({
    capsCommitted: s.capsCommitted,
    skirmAlive: Math.max(0, s.skirmAlive - loss.sk[i]), skLost: s.skLost + Math.min(s.skirmAlive, loss.sk[i]),
    capsAlive: Math.max(0, livingOf(snap.caps, i).length - capKilled[i]), capLost: s.capLost + capKilled[i],
  })) as [SideSnap, SideSnap];
  const retreat = retreatDecision([
    { capsAlive: sides[0].capsAlive, capsCommitted: sides[0].capsCommitted, stance: st[0] },
    { capsAlive: sides[1].capsAlive, capsCommitted: sides[1].capsCommitted, stance: st[1] },
  ], roundAtS - snap.openedAtS, 'round');
  return { round: r, roundAtS, opening, openingReason, changes, stances: [st[0], st[1]], mult, sk: loss.sk, capDeaths, escortDeaths, sides, retreat };
}
/** Apply a step to a snapshot (the replay's state transition — the server writes the same rows). */
export function applyRoundStep(snap: RoundSnap, step: RoundStep): RoundSnap {
  const dead = (side: number, slot: number): boolean => step.capDeaths.some((d) => d.side === side && d.slot === slot);
  const deadE = (side: number, cls: string, slot: number): boolean => step.escortDeaths.some((d) => d.side === side && d.cls === cls && d.slot === slot);
  const retreat = step.retreat.retreatSide >= 0;
  return {
    ...snap,
    rounds: snap.rounds + 1,
    state: retreat ? 'retreat' : snap.state,
    endsAtS: retreat ? Math.min(snap.endsAtS, step.roundAtS + BATTLE_END_S) : snap.endsAtS,
    sides: step.sides,
    stances: step.stances,
    caps: snap.caps.map((c) => (c.alive && dead(c.side, c.slot) ? { ...c, alive: false } : c)),
    escorts: snap.escorts.map((e) => (e.alive && deadE(e.side, e.cls, e.slot) ? { ...e, alive: false } : e)),
  };
}

// ── THE DRAIN LAW as a pure replay (chronology tests; the server's drain uses the same primitives) ──
export interface ReplayState extends RoundSnap { retreatSide: number; winner: string; log: string[] }
export type ReplayInput =
  | { kind: 'sweep'; atS: number }
  | { kind: 'kill'; atS: number; target: { kind: 'capital' | EscortClass; side: 0 | 1; slot: number } | { kind: 'gunship'; side: 0 | 1 } };
/** Resolve (the objective law's winner) — the non-retreating side on a retreat, else more capitals, else a draw. */
export function winnerOf(state: string, retreatSide: number, capsAlive: readonly [number, number]): string {
  if (state === 'retreat' && retreatSide >= 0) return retreatSide === 0 ? 'borean' : 'aurelian';
  if (capsAlive[0] !== capsAlive[1]) return capsAlive[0] > capsAlive[1] ? 'aurelian' : 'borean';
  return 'draw';
}
function drainTo(s: ReplayState, nowS: number): ReplayState {
  if (s.state !== 'engaged') return s;
  const owed = roundsEligible(s.openedAtS, s.endsAtS, nowS, s.rounds);
  for (let i = 0; i < owed && s.state === 'engaged'; i++) {
    const at = roundAtOf(s.openedAtS, s.rounds + 1);
    const step = campaignRound(s, at);
    const next = applyRoundStep(s, step);
    s = { ...s, ...next, retreatSide: step.retreat.retreatSide >= 0 ? step.retreat.retreatSide : s.retreatSide, log: [...s.log, `r${step.round}@${at}:${step.sk.join('/')}:${step.capDeaths.map((d) => `c${d.side}.${d.slot}`).join(',')}:${step.escortDeaths.map((d) => `${d.cls[0]}${d.side}.${d.slot}`).join(',')}${step.retreat.retreatSide >= 0 ? `:R${step.retreat.retreatSide}` : ''}`] };
  }
  return s;
}
/** Replay a campaign from a snapshot through sweeps and kills in time order. A sweep drains ≤ ROUND_DRAIN_CAP owed
 *  rounds, then settles only when the deadline passed AND nothing is still owed; a kill DRAINS FIRST — and when history
 *  is STILL owed after the bounded drain it is refused as pending (report §9.4: "if history remains owed: return
 *  retryable pending; apply no new damage"; the server's throw rolls the drain back too, so the replay keeps the
 *  pre-kill state) — is refused at or after the deadline, and after its death the one retreat rule judges both sides. */
export function replayCampaign(snap: RoundSnap, inputs: readonly ReplayInput[]): ReplayState {
  let s: ReplayState = { ...snap, retreatSide: -1, winner: '', log: [] };
  for (const inp of [...inputs].sort((x, y) => x.atS - y.atS)) {
    if (s.state === 'resolved') break;
    const pre = s;
    s = drainTo(s, inp.atS);
    if (inp.kind === 'kill' && s.state === 'engaged' && roundsEligible(s.openedAtS, s.endsAtS, inp.atS, s.rounds) > 0) {
      s = { ...pre, log: [...pre.log, `kill-pending@${inp.atS}`] };
      continue;
    }
    if (inp.kind === 'sweep') {
      if (inp.atS >= s.endsAtS) {
        const stillOwed = s.state === 'engaged' ? roundsEligible(s.openedAtS, s.endsAtS, inp.atS, s.rounds) : 0;
        if (stillOwed === 0) s = { ...s, state: 'resolved', winner: winnerOf(s.state, s.retreatSide, [s.sides[0].capsAlive, s.sides[1].capsAlive]), log: [...s.log, `resolved@${inp.atS}`] };
      }
      continue;
    }
    if (inp.atS >= s.endsAtS) { s = { ...s, log: [...s.log, `kill-refused@${inp.atS}`] }; continue; }
    const t = inp.target;
    const sides: [SideSnap, SideSnap] = [{ ...s.sides[0] }, { ...s.sides[1] }];
    if (t.kind === 'gunship') {
      if (sides[t.side].skirmAlive < 1) { s = { ...s, log: [...s.log, `kill-refused@${inp.atS}`] }; continue; }
      sides[t.side] = { ...sides[t.side], skirmAlive: sides[t.side].skirmAlive - 1, skLost: sides[t.side].skLost + 1 };
      s = { ...s, sides };
    } else if (t.kind === 'capital') {
      const row = s.caps.find((c) => c.side === t.side && c.slot === t.slot);
      if (!row || !row.alive) { s = { ...s, log: [...s.log, `kill-refused@${inp.atS}`] }; continue; }
      const caps = s.caps.map((c) => (c === row ? { ...c, alive: false } : c));
      sides[t.side] = { ...sides[t.side], capsAlive: livingOf(caps, t.side).length, capLost: sides[t.side].capLost + 1 };
      s = { ...s, caps, sides };
    } else {
      const row = s.escorts.find((c) => c.side === t.side && c.cls === t.kind && c.slot === t.slot);
      if (!row || !row.alive) { s = { ...s, log: [...s.log, `kill-refused@${inp.atS}`] }; continue; }
      s = { ...s, escorts: s.escorts.map((c) => (c === row ? { ...c, alive: false } : c)) };
    }
    s = { ...s, log: [...s.log, `kill@${inp.atS}:${t.kind}${t.side}${'slot' in t ? `.${t.slot}` : ''}`] };
    if (s.state === 'engaged') {
      const st: [string, string] = [s.stances[0] ?? 'screen', s.stances[1] ?? 'screen'];
      const d = retreatDecision([
        { capsAlive: s.sides[0].capsAlive, capsCommitted: s.sides[0].capsCommitted, stance: st[0] },
        { capsAlive: s.sides[1].capsAlive, capsCommitted: s.sides[1].capsCommitted, stance: st[1] },
      ], inp.atS - s.openedAtS, 'receipt');
      if (d.retreatSide >= 0) s = { ...s, state: 'retreat', retreatSide: d.retreatSide, endsAtS: Math.min(s.endsAtS, inp.atS + BATTLE_END_S), log: [...s.log, `R${d.retreatSide}@${inp.atS}`] };
    }
  }
  return s;
}

// ── THE JOURNAL'S BOUNDS ─────────────────────────────────────────────────────
/** Obligatory kinds are always written (the roster bounds them: ≤ 38 named lives a generation); optional kinds stop
 *  at the cap with ONE explicit overflow row (report §21.11: "a cap with an explicit overflow row"). */
export const JOURNAL_KINDS = ['open', 'reopen', 'death', 'retreat', 'finale', 'settlement', 'overflow', 'round', 'stance', 'pool'] as const;
export type JournalKind = (typeof JOURNAL_KINDS)[number];
export const JOURNAL_OBLIGATORY: readonly JournalKind[] = ['open', 'reopen', 'death', 'retreat', 'finale', 'settlement', 'overflow'];
export const JOURNAL_OPTIONAL_CAP = 240;
/** A lever's death is not a combat fact the war must keep: it rides the optional budget like a round summary. */
export function journalObligatory(kind: JournalKind, cause: string): boolean {
  return JOURNAL_OBLIGATORY.includes(kind) && !(kind === 'death' && cause === 'debug');
}
export function journalKey(campaignId: bigint, seq: number): string { return `${campaignId}:${seq}`; }
/** Compact counts: `caps/gunships/frigates/corvettes` per side, ` v ` between the sides (no `|`: the SQL console's
 *  column separator). */
export function countsString(c: readonly [readonly [number, number, number, number], readonly [number, number, number, number]]): string {
  return `${c[0].join('/')} v ${c[1].join('/')}`;
}
/** A late opening worth telling a pilot about (the sweep opens a second or two into every window). */
export const LATE_NOTICE_S = 60;

// ── THE AFTER-ACTION RECORD ──────────────────────────────────────────────────
export const AAR_KEEP_S = 30 * 86_400;
export const AAR_MAX_BYTES = 32 * 1024;
export const AAR_MAX_LOSSES = 64;
/** What the record admits it cannot yet prove (report §9.3 / §21.1): the reader is told, not left to assume. */
export const AAR_LIMITS: readonly string[] = [
  'hull damage short of a death is each client\'s own picture until slice D makes it canonical',
  'gunships are a pool: a gunship loss has a count, not an identity',
  'broadsides and torpedoes are presentation — only the minute\'s attrition and pilots\' receipts kill',
  'a receipt is a pilot\'s claim checked for zone, banner, slot and budget — not a proven hit',
];
/** Slice D (THE COMBAT AUTHORITY PILOT): what a record admits when its war was PINNED to validated fire — the named
 *  lives' hp was the server's, judged from intents; what stays unproven is said plainly. */
export const AAR_LIMITS_VALIDATED: readonly string[] = [
  'a capital\'s or escort\'s hull was the server\'s: every hit was a validated intent (fit damage, cadence, reach, a certified hull box at the server-derived seat) — a validated envelope over client-flown ships, not a server simulation of flight',
  'escorts fly client-side: a hit on one is checked against the theatre and the shooter\'s reach, not the escort\'s own position',
  'gunships are a pool: a gunship loss has a count, not an identity — their receipts are claims, capped per pilot and labelled so',
  'broadsides and torpedoes are presentation — only the minute\'s attrition and validated hits kill',
];
/** The authority block of a pinned war's record (absent on a claim-rules war). */
export interface AarAuthority { version: number; shots: number; hits: number; gunshipClaimCap: number }
export interface AarLoss { side: number; kind: string; slot: number; by: string; round: number; atS: number }
/** Slice H (operations with objectives): one supporting operation's settlement line — what it held, paid and refunded,
 *  its objectives (accepted of total, earned) and the posting that paid it. */
export interface AarOperation {
  opId: string; guild: string; side: number; total: number; paid: number; refund: number; cause: string; payTx: string;
  contributors: number; objectives: Array<{ template: string; accepted: number; total: number; earned: number }>;
}
/** Slice H: an escort an operation extracted (withdrawn = returned once with the survivors; failed / lost recorded). */
export interface AarExtraction { side: number; cls: string; slot: number; state: string; atS: number; hulls: number; opId: string; cancelledShots: number }
export interface AarOperations { ops: AarOperation[]; extractions: AarExtraction[] }
/** At most this many operation / extraction lines in a record (the 32 KB bound). */
export const AAR_MAX_OPS = 8;
export const AAR_MAX_EXTRACTIONS = 12;
export interface AarSide {
  civ: string;
  committed: { caps: number; sk: number; fri: number; cor: number };
  lost: { caps: number; sk: number; fri: number; cor: number };
  survivors: { caps: number; sk: number; fri: number; cor: number };
  returned: { hulls: number; caps: number };
  receipts: { n: number; points: number; paid: number; pilots: number };
}
export interface AarInput {
  campaignId: bigint; cyc: number; generation: number; rulesVersion: number; historyQuality: string; sandbox: boolean;
  /** Seconds into its window the campaign OPENED (recorded at the opening — a retreat later pulls endsAt, so the late
   *  window is never re-derived from the settled row). */
  lateOpenS: number;
  fortKey: string; fortName: string; targetSystem: string;
  openedAtS: number; endsAtS: number; settledAtS: number; rounds: number;
  winner: string; retreatSide: number; retreatCause: string;
  sov: { system: string; delta: number; applied: boolean };
  sides: [AarSide, AarSide];
  losses: AarLoss[];
  payTx: string[];
  journal: { events: number; dropped: number };
  /** Slice D: present when the war was pinned to validated fire at its opening. */
  authority?: AarAuthority;
  /** Slice H: the operations that supported this war (each settled once, with it) and its escort extractions. */
  operations?: AarOperations;
}
/** Survivors home: the gunships and capitals alive, a living frigate as four yard hulls, a corvette as two. */
export function survivorReturn(s: AarSide['survivors']): { hulls: number; caps: number } {
  return { hulls: s.sk + s.fri * HULLS_PER_FRIGATE + s.cor * HULLS_PER_CORVETTE, caps: s.caps };
}
/** A 64-bit FNV-1a over the UTF-16 code units, as 16 hex digits (accidental-change detection, not a signature). */
export function fnv1a64(s: string): string {
  let h = 0xcbf29ce484222325n;
  const P = 0x100000001b3n, M = 0xffffffffffffffffn;
  for (let i = 0; i < s.length; i++) { h ^= BigInt(s.charCodeAt(i)); h = (h * P) & M; }
  return h.toString(16).padStart(16, '0');
}
/** The record's summary JSON (≤ AAR_MAX_BYTES — losses beyond AAR_MAX_LOSSES are counted, not listed) + checksum. */
export function buildAar(inp: AarInput): { summary: string; checksum: string; bytes: number; truncated: boolean } {
  const lateS = Math.max(0, Math.min(BATTLE_LEN_S, inp.lateOpenS));
  const lw = { lateS, warS: BATTLE_LEN_S - lateS, floorReachable: BATTLE_LEN_S - lateS - BATTLE_END_S >= BATTLE_MIN_S };
  const losses = [...inp.losses].sort((x, y) => x.atS - y.atS || x.side - y.side || x.slot - y.slot);
  let listed = losses.slice(0, AAR_MAX_LOSSES);
  const body = (ls: AarLoss[]): Record<string, unknown> => ({
    v: 1,
    campaign: String(inp.campaignId), cyc: inp.cyc, generation: inp.generation, rules: inp.rulesVersion,
    quality: inp.historyQuality, sandbox: inp.sandbox,
    battlefield: { system: 'sys0', fort: inp.fortKey, name: inp.fortName },
    front: inp.targetSystem,
    open: { atS: inp.openedAtS, endsAtS: inp.endsAtS, lateS: lw.lateS, warS: lw.warS, floorReachable: lw.floorReachable },
    outcome: { winner: inp.winner, retreatSide: inp.retreatSide, cause: inp.retreatCause, rounds: inp.rounds, settledAtS: inp.settledAtS },
    sov: inp.sov,
    sides: inp.sides,
    losses: ls, lossesTotal: losses.length,
    pay: { tx: inp.payTx.slice(0, 64), txTotal: inp.payTx.length },
    journal: inp.journal,
    limits: inp.authority ? AAR_LIMITS_VALIDATED : AAR_LIMITS,
    ...(inp.authority ? { authority: inp.authority } : {}),
    ...(inp.operations && (inp.operations.ops.length || inp.operations.extractions.length)
      ? { operations: { ops: inp.operations.ops.slice(0, AAR_MAX_OPS), opsTotal: inp.operations.ops.length, extractions: inp.operations.extractions.slice(0, AAR_MAX_EXTRACTIONS) } }
      : {}),
  });
  let summary = JSON.stringify(body(listed));
  let truncated = listed.length < losses.length;
  while (summary.length > AAR_MAX_BYTES && listed.length > 0) { listed = listed.slice(0, Math.floor(listed.length / 2)); truncated = true; summary = JSON.stringify(body(listed)); }
  return { summary, checksum: fnv1a64(summary), bytes: summary.length, truncated };
}
/** Receipt points a pilot can bank in one war (the pay loop's per-pilot cap), re-exported for the record's readers. */
export const AAR_POINTS_CAP = CAMPAIGN_POINTS_CAP;
export const AAR_POINTS = CAMPAIGN_POINTS;
/** Round minutes a window of `warS` seconds can hold (a late campaign holds fewer). */
export function roundsInWindow(warS: number): number { return Math.max(0, Math.floor((warS - BATTLE_END_S) / ROUND_S)); }
